import {
  APP_INITIALIZER,
  ErrorHandler,
  enableProdMode,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr
} from '@angular/common/http';
import { RouteReuseStrategy, Router, provideRouter, withComponentInputBinding } from '@angular/router';
import { TraceService, createErrorHandler, init } from '@sentry/angular';
import { TranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideState, provideStore } from '@ngrx/store';
import {
  spxChannelEffects,
  spxChannelReducer,
} from '@softpak/components/spx-channel-selection';
import { spxConfirmEffects, spxConfirmReducer } from '@softpak/components/spx-confirm';
import {
  spxUpdateCheckEffects,
  spxUpdateCheckReducer,
  spxUpdatePendingEffects,
  spxUpdatePendingReducer,
} from '@softpak/components/spx-update';

import { AddTarEffects } from './app/store/add-tar/add-tar.effects';
import { AmqModeEffects } from './app/store/amq-mode/amq-mode.effects';
import { AmqMoveEffects } from './app/store/amq-move/amq-move.effects';
import { AmqSocketEffects } from './app/store/amq-socket/amq-socket.effects';
import { AmqTriggerEffects } from './app/store/amq-trigger/amq-trigger.effects';
import { AppComponent } from './app/app.component';
import { ApsGetEffects } from './app/store/sta-aps-get/aps-get.effects';
import { ContainerMafBundleEffects } from './app/store/maf-bundle/maf-bundle.effects';
import { ContainerMafGetEffects } from './app/store/maf-get/maf-get.effects';
import { DshGetEffects } from './app/store/dsh-get/dsh-get.effects';
import { EqsDetailEffects } from './app/store/sta-eqs-detail/eqs-detail.effects';
import { EqsGetEffects } from './app/store/sta-eqs-get/eqs-get.effects';
import { I18NLanguage } from './app/shared/_config/i18n/i18n-language';
import { I18NLoader } from './app/shared/_config/i18n/i18n-loader';
import { InfChangeDepotEffects } from './app/store/inf-change-depot/inf-change-depot.effects';
import { InfChgSaveEffects } from './app/store/inf-chg-save/inf-chg-save.effects';
import { InfLocationHistoryEffects } from './app/store/inf-location-history/inf-location-history.effects';
import { InfStkSearchEffects } from './app/store/inf-stk-search/inf-stk-search.effects';
import { IonicRouteStrategy } from '@ionic/angular';
import { MafLoadQuayEffects } from './app/store/maf-load-quay/maf-load-quay.effects';
import { MafLtcRemoveEffects } from './app/store/maf-ltc-remove/maf-ltc-remove.effects';
import { MafLtcSaveEffects } from './app/store/maf-ltc-save/maf-ltc-save.effects';
import { OrdAllocateEffects } from './app/store/ord-allocate/ord-allocate.effects';
import { OrdContainerSearchEffects } from './app/store/ord-container-search/ord-container-search.effects';
import { OrdDeallocateEffects } from './app/store/ord-deallocate-single/ord-deallocate-single.effects';
import { OrdDeallocateMultipleEffects } from './app/store/ord-deallocate-multiple/ord-deallocate-multiple.effects';
import { OrdDetailsEffects } from './app/store/ord-details/ord-details.effects';
import { OrdDetailsSecondaryEffects } from './app/store/ord-details-secondary/ord-details-secondary.effects';
import { OrdFilterEffects } from './app/store/ord-filters/ord-filters.effects';
import { OrdMultiAllocateEffects } from './app/store/ord-multi-allocate/ord-multi-allocate.effects';
import { OrdProcessEffects } from './app/store/ord-process/ord-process.effects';
import { OrdResetEffects } from './app/store/ord-reset/ord-reset.effects';
import { OrdSearchEffects } from './app/store/ord-search/ord-search.effects';
import { QuaCarriageEffects } from './app/store/qua-carriage/qua-carriage.effects';
import { QuaDetailsEffects } from './app/store/qua-details/qua-details.effects';
import { QuaDischargeEffects } from './app/store/qua-discharge/qua-discharge.effects';
import { QuaDistinctFiltersEffects } from './app/store/qua-distinct-filters/qua-distinct-filters.effects';
import { QuaFilterEffects } from './app/store/qua-filters/qua-filters.effects';
import { QuaFullLoadingListEffects } from './app/store/qua-full-loading-list/qua-full-loading-list.effects';
import { QuaLoadingEffects } from './app/store/qua-loading/qua-loading.effects';
import { QuaSearchEffects } from './app/store/qua-search/qua-search.effects';
import { RelSaveEffects } from './app/store/rel-save/rel-save.effects';
import { RfrCplSearchEffects } from './app/store/rfr-cpl-search/rfr-cpl-search.effects';
import { RfrCplLocationsEffects } from './app/store/rfr-cpl-locations/rfr-cpl-locations.effects';
import { RfrDeliveryDataEffects } from './app/store/rfr-delivery-data/rfr-delivery-data.effects';
import { RfrMonSaveEffects } from './app/store/rfr-mon-save/rfr-mon-save.effects';
import { RfrMonStackEffects } from './app/store/rfr-mon-stack/rfr-mon-stack.effects';
import { RfrMonStacksEffects } from './app/store/rfr-mon-stacks/rfr-mon-stacks.effects';
import { RfrOrdSaveEffects } from './app/store/rfr-ord-save/rfr-ord-save.effects';
import { RfrOrdSearchEffects } from './app/store/rfr-ord-search/rfr-ord-search.effects';
import { RfrSaveEffects } from './app/store/rfr-save/rfr-save.effects';
import { SHARED_PAGE_TYPE } from './app/shared/components/shared-search/shared-search.component';
import { SharedAdviseLocationEffects } from './app/store/shared-advise-location/shared-advise-location.effects';
import { SharedAuthCallEffects } from './app/store/shared-auth-call/shared-auth-call.effects';
import { SharedAuthStateEffects } from './app/store/shared-auth-state/shared-auth-state.effects';
import { SharedAutofillEffects } from './app/store/shared-autofill/shared-autofill.effects';
import { SharedCheckLocationEffects } from './app/store/shared-check-location/shared-check-location.effects';
import { SharedClipboardEffects } from './app/store/shared-clipboard/shared-clipboard.effects';
import { SharedContainerMultipleEffects } from './app/store/shared-container-multiple/shared-container-multiple.effects';
import { SharedContainerSearchEffects } from './app/store/shared-container-search/shared-container-search.effects';
import { SharedContainerSingleEffects } from './app/store/shared-container-single/shared-container-single.effects';
import { SharedDeliveryEffects } from './app/store/shared-delivery/shared-delivery.effects';
import { SharedDepotInfoEffects } from './app/store/shared-depot-info/shared-depot-info.effects';
import { SharedDepotsEffects } from './app/store/shared-depots/shared-depots.effects';
import { SharedEnvironmentInfoEffects } from './app/store/shared-environment-info/shared-environment-info.effects';
import { SharedEquipmentsEffects } from './app/store/shared-equipments/shared-equipments.effects';
import { SharedFilterEffects } from './app/store/shared-filters/shared-filters.effects';
import { SharedGetDepotEffects } from './app/store/shared-get-depot/shared-get-depot.effects';
import { SharedGetEquipmentEffects } from './app/store/shared-get-equipment/shared-get-equipment.effects';
import { SharedHttpInterceptor } from './app/shared/services/shared-http-interceptor.service';
import { SharedPageTypeEnum } from './app/shared/navigation/page-type.enum';
import { SharedSetDepotEffects } from './app/store/shared-set-depot/shared-set-depot.effects';
import { SharedSetEquipmentEffects } from './app/store/shared-set-equipment/shared-set-equipment.effects';
import { SharedSuggestionEffects } from './app/store/shared-suggestions/shared-suggestions.effects';
import { StaGetEffects } from './app/store/sta-qua-get/sta-qua-get.effects';
import { StaTrucksEffects } from './app/store/sta-trucks/sta-trucks.effects';
import { StackLocationGetEffects } from './app/store/stack-location-get/stack-location-get.effects';
import { StatsActionEffects } from './app/store/stats-action/stats-action.effects';
import { StatsConnectEffects } from './app/store/stats-connect/stats-connect.effects';
import { StatsHeartbeatEffects } from './app/store/stats-heartbeat/stats-heartbeat.effects';
import { StatsUpdateLogBufferEffects } from './app/store/stats-update-log/stats-update-log-buffer.effects';
import { StatsUpdateLogEffects } from './app/store/stats-update-log/stats-update-log.effects';
import { StatsUpdateLogTrackEffects } from './app/store/stats-update-log/stats-update-log-track.effects';
import { StatsVersionCheckEffects } from './app/store/stats-version-check/stats-version-check.effects';
import addTarReducer from './app/store/add-tar/add-tar.reducer';
import amqModeReducer from './app/store/amq-mode/amq-mode.reducer';
import amqMoveReducer from './app/store/amq-move/amq-move.reducer';
import amqSocketReducer from './app/store/amq-socket/amq-socket.reducer';
import amqTriggerReducer from './app/store/amq-trigger/amq-trigger.reducer';
import { appRoutes } from './app/shared/routing/routes/_routes.app';
import { bootstrapApplication } from '@angular/platform-browser';
import dshGetReducer from './app/store/dsh-get/dsh-get.reducer';
import { environment } from './environments/environment';
import infChangeDepotReducer from './app/store/inf-change-depot/inf-change-depot.reducer';
import infChgSaveReducer from './app/store/inf-chg-save/inf-chg-save.reducer';
import infLocationHistoryReducer from './app/store/inf-location-history/inf-location-history.reducer';
import infStkSearchReducer from './app/store/inf-stk-search/inf-stk-search.reducer';
import mafBundleReducer from './app/store/maf-bundle/maf-bundle.reducer';
import mafGetReducer from './app/store/maf-get/maf-get.reducer';
import mafLoadQuayReducer from './app/store/maf-load-quay/maf-load-quay.reducer';
import mafLtcRemoveReducer from './app/store/maf-ltc-remove/maf-ltc-remove.reducer';
import mafLtcSaveReducer from './app/store/maf-ltc-save/maf-ltc-save.reducer';
import ordAllocateReducer from './app/store/ord-allocate/ord-allocate.reducer';
import ordContainerSearchReducer from './app/store/ord-container-search/ord-container-search.reducer';
import ordDeallocateMultipleReducer from './app/store/ord-deallocate-multiple/ord-deallocate-multiple.reducer';
import ordDeallocateReducer from './app/store/ord-deallocate-single/ord-deallocate-single.reducer';
import ordDetailsReducer from './app/store/ord-details/ord-details.reducer';
import ordDetailsSecondaryReducer from './app/store/ord-details-secondary/ord-details-secondary.reducer';
import ordFiltersReducer from './app/store/ord-filters/ord-filters.reducer';
import ordMultiAllocateReducer from './app/store/ord-multi-allocate/ord-multi-allocate.reducer';
import ordProcessReducer from './app/store/ord-process/ord-process.reducer';
import ordQueryReducer from './app/store/ord-query/ord-query.reducer';
import ordResetReducer from './app/store/ord-reset/ord-reset.reducer';
import ordSearchReducer from './app/store/ord-search/ord-search.reducer';
import { provideEffects } from '@ngrx/effects';
import { provideIonicAngular } from '@ionic/angular';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import quaCarriageReducer from './app/store/qua-carriage/qua-carriage.reducer';
import quaDetailsReducer from './app/store/qua-details/qua-details.reducer';
import quaDischargeReducer from './app/store/qua-discharge/qua-discharge.reducer';
import quaDistinctFiltersReducer from './app/store/qua-distinct-filters/qua-distinct-filters.reducer';
import quaFiltersReducer from './app/store/qua-filters/qua-filters.reducer';
import quaFullLoadingListReducer from './app/store/qua-full-loading-list/qua-full-loading-list.reducer';
import quaLoadingReducer from './app/store/qua-loading/qua-loading.reducer';
import quaQueryReducer from './app/store/qua-query/qua-query.reducer';
import quaSearchReducer from './app/store/qua-search/qua-search.reducer';
import relSaveReducer from './app/store/rel-save/rel-save.reducer';
import rfrCplSearchReducer from './app/store/rfr-cpl-search/rfr-cpl-search.reducer';
import rfrCplLocationsReducer from './app/store/rfr-cpl-locations/rfr-cpl-locations.reducer';
import rfrDeliveryDataReducer from './app/store/rfr-delivery-data/rfr-delivery-data.reducer';
import rfrMonContainersReducer from './app/store/rfr-mon-stack/rfr-mon-stack.reducer';
import rfrMonSaveReducer from './app/store/rfr-mon-save/rfr-mon-save.reducer';
import rfrMonStacksReducer from './app/store/rfr-mon-stacks/rfr-mon-stacks.reducer';
import rfrOrdSaveReducer from './app/store/rfr-ord-save/rfr-ord-save.reducer';
import rfrOrdSearchReducer from './app/store/rfr-ord-search/rfr-ord-search.reducer';
import rfrSaveReducer from './app/store/rfr-save/rfr-save.reducer';
import sharedAdviseLocationReducer from './app/store/shared-advise-location/shared-advise-location.reducer';
import sharedAuthCallReducer from './app/store/shared-auth-call/shared-auth-call.reducer';
import sharedAuthStateReducer from './app/store/shared-auth-state/shared-auth-state.reducer';
import sharedAutofillReducer from './app/store/shared-autofill/shared-autofill.reducer';
import sharedChannelReducer from './app/store/shared-channel/shared-channel.reducer';
import sharedCheckLocationReducer from './app/store/shared-check-location/shared-check-location.reducer';
import sharedContainerMultipleReducer from './app/store/shared-container-multiple/shared-container-multiple.reducer';
import sharedContainerSearchReducer from './app/store/shared-container-search/shared-container-search.reducer';
import sharedContainerSingleReducer from './app/store/shared-container-single/shared-container-single.reducer';
import sharedDeliveryReducer from './app/store/shared-delivery/shared-delivery.reducer';
import sharedDepotInfoReducer from './app/store/shared-depot-info/shared-depot-info.reducer';
import sharedDepotsReducer from './app/store/shared-depots/shared-depots.reducer';
import sharedEnvironmentInfoReducer from './app/store/shared-environment-info/shared-environment-info.reducer';
import sharedEquipmentsReducer from './app/store/shared-equipments/shared-equipments.reducer';
import sharedErrorLogReducer from './app/store/shared-error-log/shared-error-log.reducer';
import sharedFiltersReducer from './app/store/shared-filters/shared-filters.reducer';
import sharedGetDepotReducer from './app/store/shared-get-depot/shared-get-depot.reducer';
import sharedGetEquipmentReducer from './app/store/shared-get-equipment/shared-get-equipment.reducer';
import sharedSetDepotReducer from './app/store/shared-set-depot/shared-set-depot.reducer';
import sharedSetEquipmentReducer from './app/store/shared-set-equipment/shared-set-equipment.reducer';
import sharedSuggestionsReducer from './app/store/shared-suggestions/shared-suggestions.reducer';
import { spxNavigationReducer } from '@softpak/components/spx-navigation';
import { spxShortCutsReducer } from '@softpak/components/spx-tabs';
import { spxSpinnerReducer } from '@softpak/components/spx-spinner';
import { spxToasterReducer } from '@softpak/components/spx-toaster';
import staApsGetReducer from './app/store/sta-aps-get/aps-get.reducer';
import staDepotReducer from './app/store/sta-depot/sta-depot.reducer';
import staEqsDetailReducer from './app/store/sta-eqs-detail/eqs-detail.reducer';
import staEqsGetReducer from './app/store/sta-eqs-get/eqs-get.reducer';
import staKioModeReducer from './app/store/sta-kio-mode/sta-kio-mode.reducer';
import staQuaGetReducer from './app/store/sta-qua-get/sta-qua-get.reducer';
import staTrucksReducer from './app/store/sta-trucks/sta-trucks.reducer';
import stackLocationGetReducer from './app/store/stack-location-get/stack-location-get.reducer';
import statsActionReducer from './app/store/stats-action/stats-action.reducer';
import statsConnectReducer from './app/store/stats-connect/stats-connect.reducer';
import statsUpdateLogReducer from './app/store/stats-update-log/stats-update-log.reducer';
import statsVersionCheckReducer from './app/store/stats-version-check/stats-version-check.reducer';

if (environment.production) {
  enableProdMode();
}

export const frontendRelease = "2.3.261030001";

init({
  dsn: 'https://750bffe7fd1d45f8b7eb73d2a4642db6@o1183852.ingest.sentry.io/6414413',
  release: frontendRelease,
  environment: environment.production ? 'production' : 'development',
  tracesSampleRate: 1.0,
  beforeBreadcrumb(breadcrumb, hint) {
    if (breadcrumb.category === 'xhr') {
      // hint.xhr is a whole XHR object that you can use to modify breadcrumb
      const xhr = hint.xhr as XMLHttpRequest;

      breadcrumb = {
        ...breadcrumb,
        data: {
          ...breadcrumb.data,
          done: JSON.stringify(xhr),
        },
      };
    }
    return breadcrumb;
  },
});

bootstrapApplication(AppComponent, {
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideIonicAngular({
      useSetInputAPI: true,
    }),
    provideZonelessChangeDetection(),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideEffects(
      AddTarEffects,
      AmqModeEffects,
      AmqMoveEffects,
      AmqSocketEffects,
      AmqTriggerEffects,
      ApsGetEffects,
      DshGetEffects,
      EqsDetailEffects,
      EqsGetEffects,
      InfChgSaveEffects,
      InfChangeDepotEffects,
      InfLocationHistoryEffects,
      InfStkSearchEffects,
      OrdAllocateEffects,
      OrdMultiAllocateEffects,
      OrdContainerSearchEffects,
      OrdDeallocateEffects,
      OrdDeallocateMultipleEffects,
      OrdDetailsEffects,
      OrdDetailsSecondaryEffects,
      OrdFilterEffects,
      OrdProcessEffects,
      OrdResetEffects,
      OrdSearchEffects,
      QuaCarriageEffects,
      QuaDetailsEffects,
      QuaDischargeEffects,
      QuaDistinctFiltersEffects,
      QuaFilterEffects,
      QuaFullLoadingListEffects,
      QuaLoadingEffects,
      QuaSearchEffects,
      RfrMonStacksEffects,
      RfrCplSearchEffects,
      RfrCplLocationsEffects,
      RelSaveEffects,
      RfrDeliveryDataEffects,
      RfrMonStackEffects,
      RfrMonSaveEffects,
      RfrOrdSaveEffects,
      RfrOrdSearchEffects,
      RfrSaveEffects,
      SharedAdviseLocationEffects,
      SharedAuthCallEffects,
      SharedAuthStateEffects,
      SharedAutofillEffects,
      SharedCheckLocationEffects,
      SharedClipboardEffects,
      SharedContainerMultipleEffects,
      SharedContainerSearchEffects,
      SharedContainerSingleEffects,
      SharedDeliveryEffects,
      SharedDepotsEffects,
      SharedEnvironmentInfoEffects,
      SharedEquipmentsEffects,
      SharedFilterEffects,
      SharedGetDepotEffects,
      SharedGetEquipmentEffects,
      SharedSetDepotEffects,
      SharedSetEquipmentEffects,
      SharedSuggestionEffects,
      MafLtcSaveEffects,
      ContainerMafGetEffects,
      MafLtcRemoveEffects,
      ContainerMafBundleEffects,
      StackLocationGetEffects,
      StaGetEffects,
      StaTrucksEffects,
      SharedDepotInfoEffects,
      MafLoadQuayEffects,
      spxConfirmEffects.SpxConfirmEffects,
      spxChannelEffects.Effects,
      spxUpdateCheckEffects.Effects,
      spxUpdatePendingEffects.Effects,
      StatsActionEffects,
      StatsConnectEffects,
      StatsHeartbeatEffects,
      StatsUpdateLogBufferEffects,
      StatsUpdateLogEffects,
      StatsUpdateLogTrackEffects,
      StatsVersionCheckEffects,
    ),
    provideStore({ router: routerReducer }),
    provideRouter(appRoutes, withComponentInputBinding()),
    provideRouterStore(),
    provideState(addTarReducer),
    provideState(amqModeReducer),
    provideState(amqMoveReducer),
    provideState(amqTriggerReducer),
    provideState(dshGetReducer),
    provideState(infChgSaveReducer),
    provideState(infChangeDepotReducer),
    provideState(infLocationHistoryReducer),
    provideState(infStkSearchReducer),
    provideState(mafBundleReducer),
    provideState(mafGetReducer),
    provideState(mafLtcRemoveReducer),
    provideState(mafLtcSaveReducer),
    provideState(ordAllocateReducer),
    provideState(ordContainerSearchReducer),
    provideState(ordDeallocateMultipleReducer),
    provideState(ordDeallocateReducer),
    provideState(ordDetailsReducer),
    provideState(ordDetailsSecondaryReducer),
    provideState(ordFiltersReducer),
    provideState(ordMultiAllocateReducer),
    provideState(ordProcessReducer),
    provideState(ordQueryReducer),
    provideState(ordResetReducer),
    provideState(ordSearchReducer),
    provideState(quaCarriageReducer),
    provideState(quaDetailsReducer),
    provideState(quaDischargeReducer),
    provideState(quaDistinctFiltersReducer),
    provideState(quaFiltersReducer),
    provideState(quaFullLoadingListReducer),
    provideState(quaLoadingReducer),
    provideState(quaQueryReducer),
    provideState(quaSearchReducer),
    provideState(relSaveReducer),
    provideState(rfrDeliveryDataReducer),
    provideState(rfrCplSearchReducer),
    provideState(rfrCplLocationsReducer),
    provideState(rfrMonContainersReducer),
    provideState(rfrMonSaveReducer),
    provideState(rfrMonStacksReducer),
    provideState(rfrOrdSaveReducer),
    provideState(rfrOrdSearchReducer),
    provideState(rfrSaveReducer),
    provideState(amqSocketReducer),
    provideState(sharedAdviseLocationReducer),
    provideState(sharedAuthCallReducer),
    provideState(sharedAuthStateReducer),
    provideState(sharedChannelReducer),
    provideState(sharedAutofillReducer),
    provideState(sharedCheckLocationReducer),
    provideState(sharedContainerMultipleReducer),
    provideState(sharedContainerSearchReducer),
    provideState(sharedContainerSingleReducer),
    provideState(sharedDeliveryReducer),
    provideState(sharedDepotsReducer),
    provideState(sharedEnvironmentInfoReducer),
    provideState(sharedEquipmentsReducer),
    provideState(sharedErrorLogReducer),
    provideState(sharedFiltersReducer),
    provideState(sharedGetDepotReducer),
    provideState(sharedGetEquipmentReducer),
    provideState(sharedSetDepotReducer),
    provideState(sharedSetEquipmentReducer),
    provideState(sharedSuggestionsReducer),
    provideState(staApsGetReducer),
    provideState(staDepotReducer),
    provideState(staEqsDetailReducer),
    provideState(staEqsGetReducer),
    provideState(staKioModeReducer),
    provideState(staQuaGetReducer),
    provideState(staTrucksReducer),
    provideState(mafLoadQuayReducer),
    provideState(stackLocationGetReducer),
    provideState(sharedDepotInfoReducer),
    provideState(statsActionReducer),
    provideState(statsConnectReducer),
    provideState(statsUpdateLogReducer),
    provideState(statsVersionCheckReducer),
    provideState(spxConfirmReducer.default),
    provideState(spxChannelReducer.default),
    provideState(spxNavigationReducer.default),
    provideState(spxSpinnerReducer.default),
    provideState(spxToasterReducer.default),
    provideState(spxUpdateCheckReducer.default),
    provideState(spxUpdatePendingReducer.default),
    provideState(spxShortCutsReducer.default),
    provideStoreDevtools({
      autoPause: true,
      maxAge: 500,
      logOnly: environment.production,
      trace: true,
      traceLimit: 75,
    }),
    provideTranslateService({
      lang: I18NLanguage.english,
      loader: {
        provide: TranslateLoader,
        useClass: I18NLoader,
        deps: [HttpClient],
      },
    }),
    { provide: SHARED_PAGE_TYPE, useValue: SharedPageTypeEnum.infCnt },
    {
      provide: ErrorHandler,
      useValue: createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: TraceService,
      deps: [Router],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SharedHttpInterceptor,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => { },
      deps: [TraceService],
      multi: true,
    },
  ],
}).catch((err) => console.log(err));
